import React, { useEffect, useState } from 'react';

import { Modal, Form, Badge } from 'react-bootstrap';
import Button from 'components/base/Button';
import { FolderSelect } from 'components/forms/FolderSelect';
import type { CreateTarget } from 'actions';
import api from 'services/api';

import { useAppDispatch } from 'redux/store';
import { createPythonLibrary, updatePythonLibrary, fetchPythonLibraryById } from '../../../../redux/slices/pythonLibrariesSlice';
import { useUnsavedChanges } from '../../../../hooks/useUnsavedChanges';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';

export interface EditPythonLibraryModalProps {
  show: boolean;
  handleClose: () => void;
  mode: 'create' | 'edit';
  libraryId?: number;
  // Workspace create pre-target: seeds the folder on create (python libraries are zoneless).
  createIn?: CreateTarget;
}

interface LibraryForm {
  python_library_id?: number;
  name: string;
  filename: string;
  description: string;
  content: string;
  folder_id: number | null;
}

interface NodeDef {
  node_definition_id: number;
  name: string;
  type: string;
}

const EMPTY_FORM: LibraryForm = { name: '', filename: '', description: '', content: '', folder_id: null };

/**
 * Create / edit a Python library. Self-contained: loads the full record by id
 * for edit, fetches the norscom_python node definitions and the ones currently
 * linked (toggled here), persists the library, and (on edit) saves the node-def
 * links. Can be opened from anywhere.
 */
const EditPythonLibraryModal: React.FC<EditPythonLibraryModalProps> = ({ show, handleClose, mode, libraryId, createIn }) => {
  const dispatch = useAppDispatch();

  const [form, setForm] = useState<LibraryForm>(EMPTY_FORM);
  const [allNodeDefs, setAllNodeDefs] = useState<NodeDef[]>([]);
  const [linkedNodeDefIds, setLinkedNodeDefIds] = useState<number[]>([]);
  const [loadingNodeDefs, setLoadingNodeDefs] = useState(false);
  const [isEditFormInitialized, setIsEditFormInitialized] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isDirty = useUnsavedChanges(show && (mode === 'create' || isEditFormInitialized), mode === 'create' ? form : { form, linkedNodeDefIds });

  // Initialise on open: a blank create form, or load the record + node-def links for edit.
  useEffect(() => {
    if (!show) return;
    if (mode !== 'edit' || libraryId == null) {
      setForm({ ...EMPTY_FORM, folder_id: createIn?.folderId ?? null });
      setLinkedNodeDefIds([]);
      setAllNodeDefs([]);
      setIsEditFormInitialized(false);
      return;
    }

    setIsEditFormInitialized(false);
    setLinkedNodeDefIds([]);
    setAllNodeDefs([]);

    void (async () => {
      const result: any = await dispatch(fetchPythonLibraryById(libraryId) as any);
      const fullLib = result.payload;
      if (!fullLib) {
        setIsEditFormInitialized(true);
        return;
      }
      setForm({
        python_library_id: fullLib.python_library_id,
        name: fullLib.name,
        filename: fullLib.filename,
        description: fullLib.description || '',
        content: fullLib.content || '',
        folder_id: fullLib.folder_id ?? null,
      });
      setLoadingNodeDefs(true);
      try {
        const [allRes, linkedRes] = await Promise.all([
          api.get('/node_definitions', { params: { type: 'norscom_python', limit: 1000, offset: 0 } }),
          api.get(`/python_libraries/${fullLib.python_library_id}/node_definitions`),
        ]);
        const allDefs = Array.isArray(allRes.data) ? allRes.data : allRes.data?.nodes || allRes.data?.rows || [];
        setAllNodeDefs(allDefs);
        setLinkedNodeDefIds((linkedRes.data || []).map((nd: any) => nd.node_definition_id));
      } catch (err) {
        console.error('Failed to fetch node definitions', err);
      } finally {
        setLoadingNodeDefs(false);
        setIsEditFormInitialized(true);
      }
    })();
  }, [show, mode, libraryId, dispatch, createIn]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleNodeDefToggle = (nodeDefId: number, checked: boolean) => {
    setLinkedNodeDefIds((prev) => (checked ? [...prev, nodeDefId] : prev.filter((id) => id !== nodeDefId)));
  };

  const resetAndClose = () => {
    setForm(EMPTY_FORM);
    setLinkedNodeDefIds([]);
    setAllNodeDefs([]);
    setIsEditFormInitialized(false);
    handleClose();
  };

  const handleRequestClose = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      resetAndClose();
    }
  };

  const canSave = !!form.name && !!form.filename && !!form.content;

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      if (mode === 'edit' && form.python_library_id) {
        const result = await dispatch(
          updatePythonLibrary({
            python_library_id: form.python_library_id,
            name: form.name,
            filename: form.filename,
            description: form.description || undefined,
            content: form.content,
            folder_id: form.folder_id,
          }) as any
        );
        if (updatePythonLibrary.rejected.match(result)) return;
        try {
          await api.put(`/python_libraries/${form.python_library_id}/node_definitions`, { node_definition_ids: linkedNodeDefIds });
        } catch (err) {
          console.error('Failed to update node definition links', err);
        }
      } else {
        const result = await dispatch(
          createPythonLibrary({
            name: form.name,
            filename: form.filename,
            description: form.description || undefined,
            content: form.content,
            folder_id: form.folder_id,
          }) as any
        );
        if (createPythonLibrary.rejected.match(result)) return;
      }
      resetAndClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const libraryForm = (
    <Form>
      <Form.Group className="mb-3">
        <Form.Label>Name</Form.Label>
        <Form.Control type="text" placeholder="Display name (e.g. My Utilities)" name="name" value={form.name} onChange={handleChange} />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Filename</Form.Label>
        <Form.Control type="text" placeholder="my_utils.py" name="filename" value={form.filename} onChange={handleChange} />
        <Form.Text className="text-muted">Must end with .py — this is the import name used in scripts.</Form.Text>
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Description</Form.Label>
        <Form.Control type="text" placeholder="Optional description" name="description" value={form.description} onChange={handleChange} />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Folder</Form.Label>
        <FolderSelect value={form.folder_id} onChange={(folder_id) => setForm((prev) => ({ ...prev, folder_id }))} />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Content</Form.Label>
        <Form.Control
          as="textarea"
          rows={12}
          name="content"
          value={form.content}
          onChange={handleChange}
          style={{ fontFamily: '"Fira Code", "Fira Mono", monospace', fontSize: 13 }}
          placeholder="# Python code here..."
        />
      </Form.Group>
    </Form>
  );

  return (
    <>
      <Modal show={show} onHide={handleRequestClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{mode === 'edit' ? 'Edit Python Library' : 'Create Python Library'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {mode === 'edit' && !isEditFormInitialized && loadingNodeDefs ? (
            <div className="text-muted small">Loading node definitions...</div>
          ) : (
            <>
              {libraryForm}

              {mode === 'edit' && (
                <>
                  <hr />
                  <h6>Linked Node Definitions {linkedNodeDefIds.length > 0 && <Badge bg="primary">{linkedNodeDefIds.length}</Badge>}</h6>
                  {loadingNodeDefs ? (
                    <div className="text-muted small">Loading node definitions...</div>
                  ) : allNodeDefs.length === 0 ? (
                    <div className="text-muted small">No norscom_python node definitions found.</div>
                  ) : (
                    <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '0.25rem', padding: '0.5rem' }}>
                      {allNodeDefs.map((nd) => (
                        <Form.Check
                          key={nd.node_definition_id}
                          type="checkbox"
                          id={`nd-${nd.node_definition_id}`}
                          label={nd.name}
                          checked={linkedNodeDefIds.includes(nd.node_definition_id)}
                          onChange={(e) => handleNodeDefToggle(nd.node_definition_id, e.target.checked)}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleRequestClose}>
            Close
          </Button>
          <Button variant="primary" disabled={!canSave || isSubmitting || (mode === 'edit' && !isEditFormInitialized)} onClick={handleSave}>
            {mode === 'edit' ? 'Save and Close' : 'Create Library'}
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmationModal
        show={showCloseConfirm}
        title="Unsaved Changes"
        body="You have unsaved changes. Are you sure you want to close without saving?"
        confirmText="Discard Changes"
        confirmVariant="danger"
        onConfirm={() => {
          setShowCloseConfirm(false);
          resetAndClose();
        }}
        onCancel={() => setShowCloseConfirm(false)}
      />
    </>
  );
};

export default EditPythonLibraryModal;
